import { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

/**
 * The tenant's own lender panel, taken from their commission data.
 *
 * WHY NOT A HARDCODED LIST
 *
 * A fixed list of big-four-plus-a-few is wrong twice over. It is not the
 * tenant's actual panel, and it reads as a restriction even where the field
 * accepts free text, so a broker whose lender is missing assumes the feature
 * cannot handle it.
 *
 * It also degrades settlement matching. The reconciler corroborates a lodgement
 * against raw_commissions partly on lender, so a suggestion list spelled
 * differently to the commission pipeline ("St George" vs "St.George") produces
 * lodgements that fail to corroborate. Sourcing the suggestions FROM that same
 * pipeline means the spelling agrees by construction.
 *
 * `getDistinctLenders` already exists and already serves the Loan Search filter,
 * with company scoping, office/broker narrowing and marketing denial enforced
 * server-side. Reused rather than reimplemented.
 *
 * The field stays free text. This supplies suggestions, never a whitelist: a
 * lender new to the tenant has no commission rows yet and must still be typeable
 * on the day the deal is lodged.
 */

/**
 * Last-resort suggestions, used only when the tenant has no commission history
 * to derive a panel from (a brand new tenant, or a stage environment with no
 * loaded data). Deliberately short: it is a starting point, not a panel.
 */
const FALLBACK_LENDERS = [
  'ANZ', 'Bankwest', 'Bendigo', 'CBA', 'ING', 'Macquarie',
  'NAB', 'St George', 'Suncorp', 'ubank', 'Westpac',
];

export function useTenantLenders(companyId: string | undefined) {
  const [lenders, setLenders] = useState<string[]>(FALLBACK_LENDERS);
  const [source, setSource] = useState<'tenant' | 'fallback'>('fallback');
  const [loading, setLoading] = useState<boolean>(Boolean(companyId));

  useEffect(() => {
    if (!companyId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const call = httpsCallable<{ companyId: string }, string[]>(functions, 'getDistinctLenders');
    call({ companyId })
      .then((res) => {
        if (cancelled) return;
        const rows = (res.data ?? []).filter((l) => typeof l === 'string' && l.trim() !== '');
        if (rows.length > 0) {
          setLenders([...rows].sort((a, b) => a.localeCompare(b)));
          setSource('tenant');
        }
        // No rows: keep the fallback rather than presenting an empty list.
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        // Non-fatal. Suggestions are a convenience and the field is free text,
        // so a failure here must not block capture. Logged rather than
        // swallowed, so it does not silently look like "this tenant has no
        // lenders".
        console.error('[useTenantLenders] falling back to default list', e);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  return { lenders, source, loading };
}
